import { Component, OnInit } from '@angular/core';

interface OnlineUser {
  id: number;
  name: string;
  email: string;
  loginTime: Date;
  lastActivity: Date;
  timeLastActivity: string;
  userType: string;
  inactivityTime: string;
  status: 'active' | 'idle' | 'away';
}

@Component({
  selector: 'app-online-user',
  templateUrl: './online-user.component.html',
  styleUrls: ['./online-user.component.scss']
})
export class OnlineUserComponent implements OnInit {

  Math = Math; // Make Math available in template
  onlineUsers: OnlineUser[] = [];
  filteredUsers: OnlineUser[] = [];
  searchTerm: string = '';
  selectedUserType: string = 'all';
  selectedStatus: string = 'all';

  // Pagination
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalItems: number = 0;

  userTypes: string[] = ['Administrator', 'Teacher', 'Student', 'Support'];
  statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'idle', label: 'Idle' },
    { value: 'away', label: 'Away' }
  ];

  constructor() { }

  ngOnInit(): void {
    this.loadOnlineUsers();
  }

  loadOnlineUsers(): void {
    // Sample data - replace with actual service call
    this.onlineUsers = [
      {
        id: 1,
        name: 'John Doe',
        email: 'john.doe@example.com',
        loginTime: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
        lastActivity: new Date(Date.now() - 5 * 60 * 1000), // 5 minutes ago
        timeLastActivity: '5 minutes ago',
        userType: 'Administrator',
        inactivityTime: '5 minutes',
        status: 'active'
      },
      {
        id: 2,
        name: 'Jane Smith',
        email: 'jane.smith@example.com',
        loginTime: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
        lastActivity: new Date(Date.now() - 15 * 60 * 1000), // 15 minutes ago
        timeLastActivity: '15 minutes ago',
        userType: 'Teacher',
        inactivityTime: '15 minutes',
        status: 'idle'
      },
      {
        id: 3,
        name: 'Mike Johnson',
        email: 'mike.johnson@example.com',
        loginTime: new Date(Date.now() - 1 * 60 * 60 * 1000), // 1 hour ago
        lastActivity: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
        timeLastActivity: '30 minutes ago',
        userType: 'Student',
        inactivityTime: '30 minutes',
        status: 'away'
      },
      {
        id: 4,
        name: 'Sarah Wilson',
        email: 'sarah.wilson@example.com',
        loginTime: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
        lastActivity: new Date(Date.now() - 2 * 60 * 1000), // 2 minutes ago
        timeLastActivity: '2 minutes ago',
        userType: 'Support',
        inactivityTime: '2 minutes',
        status: 'active'
      },
      {
        id: 5,
        name: 'David Brown',
        email: 'david.brown@example.com',
        loginTime: new Date(Date.now() - 3 * 60 * 60 * 1000), // 3 hours ago
        lastActivity: new Date(Date.now() - 1 * 60 * 1000), // 1 minute ago
        timeLastActivity: '1 minute ago',
        userType: 'Teacher',
        inactivityTime: '1 minute',
        status: 'active'
      }
    ];

    this.applyFilters();
  }

  applyFilters(): void {
    this.filteredUsers = this.onlineUsers.filter(user => {
      const matchesSearch = !this.searchTerm ||
        user.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(this.searchTerm.toLowerCase());

      const matchesUserType = this.selectedUserType === 'all' || user.userType === this.selectedUserType;
      const matchesStatus = this.selectedStatus === 'all' || user.status === this.selectedStatus;

      return matchesSearch && matchesUserType && matchesStatus;
    });

    this.totalItems = this.filteredUsers.length;
    this.currentPage = 1;
  }

  onSearch(): void {
    this.applyFilters();
  }

  onUserTypeChange(): void {
    this.applyFilters();
  }

  onStatusChange(): void {
    this.applyFilters();
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'active': return 'bg-success';
      case 'idle': return 'bg-warning';
      case 'away': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  getUserTypeClass(userType: string): string {
    switch (userType) {
      case 'Administrator': return 'badge bg-primary';
      case 'Teacher': return 'badge bg-info';
      case 'Student': return 'badge bg-success';
      case 'Support': return 'badge bg-warning';
      default: return 'badge bg-secondary';
    }
  }

  refreshData(): void {
    this.loadOnlineUsers();
  }

  get paginatedUsers(): OnlineUser[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredUsers.slice(startIndex, endIndex);
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  get pageNumbers(): number[] {
    const pages = [];
    for (let i = 1; i <= this.totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  trackByUserId(index: number, user: OnlineUser): number {
    return user.id;
  }

  getActiveUsersCount(): number {
    return this.onlineUsers.filter(user => user.status === 'active').length;
  }

  getIdleUsersCount(): number {
    return this.onlineUsers.filter(user => user.status === 'idle').length;
  }

  getAwayUsersCount(): number {
    return this.onlineUsers.filter(user => user.status === 'away').length;
  }
}
